import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { teamService, ROLES } from '../services/teamService';
import { TopBar } from '../components/common/TopBar';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import './Study.css';

// Modalità disponibili per ruolo
const ALL_MODES = [
  {
    id: 'flashcards',
    icon: '🃏',
    title: 'Flashcard Guesser',
    desc: 'Vedi la frase → ricorda la parola. Per chi deve indovinare.',
    path: '/study/flashcards',
    color: '#FFCC00',
    allowedRoles: ['guesser'],
  },
  {
    id: 'dialogue',
    icon: '💬',
    title: 'Dialoghi Speaker',
    desc: 'Allenati sulla sequenza del dialogo tra primo e secondo speaker.',
    path: '/study/dialogue',
    color: '#FF8800',
    allowedRoles: ['speaker1', 'speaker2'],
  },
  {
    id: 'phrases',
    icon: '✏️',
    title: 'Gestisci Frasi',
    desc: 'Inserisci e modifica le frasi associate a ogni parola.',
    path: '/phrases',
    color: '#22C55E',
    allowedRoles: ['speaker1', 'speaker2', 'guesser'], // tutti
  },
];


// ── Study Page ─────────────────────────────────────────────
const Study = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const teamDropdownRef = useRef(null);

  const loadTeams = useCallback(async () => {
    if (!currentUser) { setLoading(false); return; }
    try {
      const [created, joined] = await Promise.all([
        teamService.getTeamsByUser(currentUser.uid),
        teamService.getAllTeamsByUser(currentUser.uid),
      ]);
      const map = {};
      [...created, ...joined].forEach((t) => { map[t.id] = t; });
      // Attach _myRole for convenience
      const all = Object.values(map).map((t) => ({
        ...t,
        _myRole: teamService.getUserRole(t, currentUser.uid),
      }));
      setTeams(all);
      // Auto-select if teams exist
      if (all.length > 0) {
        setSelectedTeam(all[0]);
        setMyRole(all[0]._myRole);
      }
    } catch (e) {
      console.error('Error loading teams:', e);
    }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  // ── Click outside to close team dropdown ───────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target)) {
        setTeamDropdownOpen(false);
      }
    };

    if (teamDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [teamDropdownOpen]);


  // ── Loading ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="study-hub">
        <TopBar title="Studio" onBack={() => navigate('/')} />
        <LoadingSpinner fullPage />
      </div>
    );
  }

  // ── No Teams ───────────────────────────────────────────
  if (teams.length === 0) {
    return (
      <div className="study-hub">
        <TopBar title="Studio" onBack={() => navigate('/')} />
        <div className="study-hub__content study-hub__empty">
          <span className="study-hub__empty-icon">🏆</span>
          <p className="study-hub__empty-text">Unisciti a una squadra per studiare.</p>
          <button className="btn-premium" onClick={() => navigate('/teams')} id="btn-go-teams">
            Vai a Squadre →
          </button>
        </div>
      </div>
    );
  }

  // ── Mode Selection (role-gated) ────────────────────────
  return (
    <div className="study-hub">
      <TopBar
        title="Studio"
        onBack={() => navigate('/')}
        actions={
          <div className="study-team-dropdown-wrap" ref={teamDropdownRef}>
            <button
              className="study-team-pill"
              onClick={() => setTeamDropdownOpen((v) => !v)}
              id="btn-select-team"
            >
              🏆 {selectedTeam ? selectedTeam.name : 'Scegli la squadra'}
              <span className="study-team-pill__caret">{teamDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {teamDropdownOpen && (
              <div className="study-team-dropdown">
                {teams.map((t) => {
                  const roleInfo = ROLES.find((r) => r.value === t._myRole);
                  return (
                    <button
                      key={t.id}
                      className={`study-team-dropdown__item ${selectedTeam?.id === t.id ? 'is-active' : ''}`}
                      onClick={() => {
                        setSelectedTeam(t);
                        setMyRole(t._myRole);
                        setTeamDropdownOpen(false);
                      }}
                    >
                      <span className="study-team-dropdown__name">{t.name}</span>
                      {roleInfo && (
                        <span className="study-team-dropdown__role">
                          {roleInfo.icon} {roleInfo.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        }
      />
      <div className="study-hub__content">
        {myRole ? (
          <>
            {ALL_MODES.map((m) => {
              const isAllowed = m.allowedRoles.includes(myRole);
              return (
                <button
                  key={m.id}
                  className={`study-mode-card ${!isAllowed ? 'study-mode-card--locked' : ''}`}
                  onClick={() => {
                    if (!isAllowed) return;
                    if (m.id === 'phrases') {
                      navigate(m.path, { state: { teamId: selectedTeam.id, teamName: selectedTeam.name } });
                    } else {
                      navigate(m.path);
                    }
                  }}
                  id={`btn-study-${m.id}`}
                  style={{ '--mode-color': m.color }}
                  disabled={!isAllowed}
                >
                  <span className="study-mode-card__icon">{m.icon}</span>
                  <div className="study-mode-card__info">
                    <h2 className="study-mode-card__title">{m.title}</h2>
                    <p className="study-mode-card__desc">
                      {isAllowed
                        ? m.desc
                        : `Non disponibile per ${ROLES.find((r) => r.value === myRole)?.label}`}
                    </p>
                  </div>
                  <span className="study-mode-card__arrow">
                    {isAllowed ? '›' : '🔒'}
                  </span>
                </button>
              );
            })}
          </>
        ) : (
          <div className="study-hub__empty">
            <span className="study-hub__empty-icon">⚠️</span>
            <p>Non hai ancora un ruolo assegnato in questa squadra.</p>
            <button
              className="btn-premium"
              onClick={() => navigate(`/teams/${selectedTeam.id}`)}
              id="btn-go-team-detail"
            >
              Vai alla Squadra →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Study;
